import cn from 'classnames';
import { ClientEvents } from 'common/events/ClientEvents';
import { formatCurrency } from 'common/FormatUtilities';
import { abbreviateNumber } from 'common/NumberUtils';
import { assertDateString } from 'common/UrlDate';
import { map, sumBy, uniq } from 'lodash';
import { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ToastPosition } from 'react-toastify';
import { DishFirstFoodItemFragment } from 'src/gqlReactTypings.generated.d';
import { dishFirstFoodItemFragmentToCartShef } from 'src/pages/consumer/dish-first/explore/components/Segments/DishFirstModal/hooks/useFoodItemAvailability';
import { ButtonColors, SecondaryButton } from 'src/shared/design-system/Buttons';
import { MoreFoodItem } from 'src/shared/design-system/DishFirst/DishModal/components/MoreFoodItem';
import { getShefMenuUrl } from 'src/shared/design-system/DishFirst/DishModal/utils';
import { FaArrowRotate } from 'src/shared/design-system/FontAwesome';
import { ThumbsUpV2 } from 'src/shared/design-system/Icon';
import { useTracker } from 'src/shared/hooks/useTracker';
import Notifications from 'src/shared/Notifications';
import { useDispatch, useSelector } from 'src/store';
import { decLineItemQuantity, incLineItemQuantity, setShefs } from 'src/store/cart/actions';
import { selectAvailableLineItems, selectCartGrandSubtotal, selectLineItemQuantities } from 'src/store/cart/selectors';
import { CartActionLocation, CartFoodItem } from 'src/store/cart/types';
import { match } from 'ts-pattern';
import { PreCheckoutUpsellModalNoMoreResults } from './PreCheckoutUpsellModalNoMoreResults';

const LoadingDots: React.FC<{ className?: string }> = ({ className, ...props }) => (
  <div className={cn(className, 'flex')} {...props}>
    <div
      className='mr-1.5 size-2 animate-[pulse_1.5s_ease_infinite] rounded-full bg-neutral-400'
      key='loading-dots[0]'
    />
    <div
      className='mr-1.5 size-2 animate-[pulse_1.5s_ease_infinite_.5s] rounded-full bg-neutral-400'
      key='loading-dots[1]'
    />
    <div className='size-2 animate-[pulse_1.5s_ease_infinite_1s] rounded-full bg-neutral-400' key='loading-dots[2]' />
  </div>
);

interface PreCheckoutUpsellModalBodyProps {
  suggestions: DishFirstFoodItemFragment[];
  deliveryDate: string;
  showNextSuggestions: () => void;
  onSuggestionClick: (suggestion: DishFirstFoodItemFragment) => void;
  loading: boolean;
  hide: () => void;
}

export const PreCheckoutUpsellModalBody: React.FC<PreCheckoutUpsellModalBodyProps> = ({
  suggestions,
  deliveryDate,
  showNextSuggestions,
  onSuggestionClick,
  loading,
  hide,
}) => {
  const tracker = useTracker();

  const dispatch = useDispatch();
  const { lineItemQuantities, subtotal, availableLineItems } = useSelector(({ cart }) => ({
    lineItemQuantities: selectLineItemQuantities(cart),
    availableLineItems: selectAvailableLineItems(cart),
    subtotal: selectCartGrandSubtotal(cart),
  }));

  const suggestionSubtotal = sumBy(suggestions, 'price');

  const addToCart = useCallback(
    (
      foodItem: CartFoodItem,
      quantity: number,
      deliveryDate: string,
      location: CartActionLocation,
      skipNotification?: boolean
    ) => {
      const suggestion = suggestions.find((suggestion) => suggestion.foodItemId === foodItem.id);
      if (suggestion) dispatch(setShefs([dishFirstFoodItemFragmentToCartShef(suggestion)]));
      if (quantity < 0) {
        dispatch(decLineItemQuantity(false, { foodItem, deliveryDate, location }));
      } else {
        dispatch(incLineItemQuantity(false, { foodItem, deliveryDate, location }));
      }
      const existingQuantity = lineItemQuantities[suggestion?.foodItemId ?? -1] ?? 0;
      const isIncreasing = quantity > existingQuantity;
      const shouldNotify = !skipNotification && isIncreasing;
      if (shouldNotify) Notifications.success('Added to cart', { position: ToastPosition.BOTTOM_CENTER });
    },
    [dispatch, suggestions, lineItemQuantities]
  );

  const addAllToCart = useCallback(() => {
    suggestions.forEach((suggestion) => {
      addToCart({ ...suggestion, id: suggestion.foodItemId }, 1, deliveryDate, 'pre-checkout-upsell-modal', true);
    });
    Notifications.success('Added to cart', { position: ToastPosition.BOTTOM_CENTER });

    showNextSuggestions();

    tracker.track(ClientEvents.PRE_CHECKOUT_UPSELL_ADD_ALL, {
      foodItemIdsInCart: map(availableLineItems, 'foodItemId'),
      numFoodItemsInCart: sumBy(availableLineItems, 'quantity'),
      shefIdsInCart: uniq(map(availableLineItems, 'shefId')),
      cartSubtotal: subtotal,
      upsellFoodItemIds: map(suggestions, 'foodItemId'),
      upsellShefIds: uniq(map(suggestions, 'shefId')),
      upsellSubtotal: suggestionSubtotal,
    });
  }, [addToCart, suggestions, showNextSuggestions, tracker, suggestionSubtotal, suggestions, availableLineItems]);

  return (
    <div className='mx-auto mb-8 flex w-80 flex-col items-center text-radish-200'>
      {!loading && suggestions.length === 0 ? (
        <PreCheckoutUpsellModalNoMoreResults />
      ) : (
        <>
          <div className='mb-8 flex w-full'>
            {loading
              ? [1, 2, 3].map((index) => (
                  <div key={index} className='mr-4 flex w-24 flex-col'>
                    <div className='size-24 animate-pulse rounded-2xl bg-neutral-100' />
                    <div className='mt-2.5 h-9 w-20 animate-pulse rounded-lg bg-neutral-100' />
                    <div className='mt-2.5 h-4 w-16 animate-pulse rounded-lg bg-neutral-100' />
                    <div className='mt-2.5 h-9 w-20 animate-pulse rounded-lg bg-neutral-100' />
                    <div className='mb-1.5 mt-2.5 h-4 w-16 animate-pulse rounded-lg bg-neutral-100' />
                  </div>
                ))
              : suggestions.map((suggestion) => (
                  <div key={suggestion.foodItemId} className='mr-4 flex w-24 flex-col'>
                    <MoreFoodItem
                      className='!ml-0 mb-2'
                      location='pre-checkout-upsell-modal'
                      deliveryDate={assertDateString(deliveryDate)}
                      foodItem={suggestion}
                      quantity={lineItemQuantities[suggestion.foodItemId] ?? 0}
                      onClick={() => onSuggestionClick(suggestion)}
                      onChangeFoodItemQuantity={addToCart}
                    />
                    <div>
                      <div className='no-wrap mb-2 inline-flex h-8 shrink-0 items-center rounded-md bg-jackfruit-50 px-2 text-neutral-700'>
                        <ThumbsUpV2 width={14} height={14} className='mb-1 mr-1' />
                        <span className='text-xs font-medium text-neutral-700'>
                          {abbreviateNumber(suggestion.averageRating)}%{' '}
                          {suggestion.ratingCount ? ` (${abbreviateNumber(suggestion.ratingCount)})` : ``}
                        </span>
                      </div>
                    </div>
                    <Link
                      to={getShefMenuUrl(suggestion.shef.id)}
                      onClick={() => hide()}
                      className='font-medium text-neutral-700 hover:text-neutral-800 hover:underline '>
                      Shef {suggestion.shef.publicName}
                    </Link>
                  </div>
                ))}
          </div>
          <SecondaryButton color={ButtonColors.RED} className='w-full !rounded-lg' onClick={addAllToCart}>
            {match({ loading, count: suggestions.length })
              .with({ loading: true }, () => <LoadingDots />)
              .with({ count: 0 }, () => ``) // shouldn't happen...
              .with({ count: 1 }, () => `Add to cart for ${formatCurrency(suggestionSubtotal)}`)
              .otherwise(() => `Add all ${suggestions.length} to cart for ${formatCurrency(suggestionSubtotal)}`)}
          </SecondaryButton>
          <button
            className='mt-6 border-none bg-transparent text-sm font-semibold text-neutral-700'
            onClick={showNextSuggestions}>
            <FaArrowRotate className='mr-1.5 text-xs' /> <span className='underline'>Refresh recommendations</span>
          </button>
        </>
      )}
    </div>
  );
};
